import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Grid } from '@mui/material';
import { HSpacer, RoutalAvatar, RoutalDialog, RoutalPalette, RoutalSearchInput, VSpacer } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import ProjectUserIcon from '../../../../assets/svg/ProjectUserIcon';
import ProjectVehicleIcon from '../../../../assets/svg/ProjectVehicleIcon';
import * as Routing from '../../../../constants/Routing';
import { FlatProject } from '../../../../domain/project/FlatProject';
import { Project } from '../../../../domain/project/Project';
import { OrganizationResources } from '../../../../domain/resources/OrganizationResources';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import { getUploadAsBase64 } from '../../../../services/requests';

interface SwitchProjectDialogProps {
  open: boolean;
  activeProject: Project;
  projects: FlatProject[];
  organizationResources: OrganizationResources;
  onClose?: () => void;
}

export const SwitchProjectDialog = ({
  open,
  projects,
  activeProject,
  organizationResources,
  onClose,
}: SwitchProjectDialogProps) => {
  const [searchText, setSearchText] = useState(``);

  const visibleProjects = useMemo(() => {
    if (!projects) return [];
    return projects
      .filter((project) => (project.label ?? ``).toLowerCase().includes(searchText.toLowerCase()))
      .sort((pA, pB) => {
        if (pA.label && pB.label) {
          return pA.label.localeCompare(pB.label);
        } else if (pA.label) {
          return -1;
        } else if (pB.label) {
          return 1;
        } else {
          return 0;
        }
      });
  }, [projects, searchText]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearchText = useCallback(
    _.debounce((text: any) => {
      setSearchText(text);
    }, 300),
    []
  );
  return (
    <RoutalDialog
      open={open}
      onCloseDialog={() => {
        // if (!loading) onCloseDialog();
      }}
      showCrossCloseButton={false}
      titleContentStyles={{ display: `flex`, width: `100%`, justifyContent: `space-between`, alignItems: `center` }}
      titleContent={
        <>
          <Text variant="h6" intlId={`sidebar.project.dialog.title`} />
          <RoutalSearchInput
            inputContainerStyles={{
              width: `260px`,
              borderRadius: `10px`,
            }}
            bGColor="white"
            fieldValue={searchText}
            onChange={debouncedSetSearchText}
            searchLive
          />
        </>
      }
      footerContent={
        <Grid container justifyContent="flex-end">
          <Button
            id="cancel"
            // disabled={loading}
            onClick={() => {
              onClose && onClose();
            }}
            intlId={`cancel`}
          />
        </Grid>
      }
    >
      <Grid style={{ height: `500px` }}>
        {visibleProjects.map((project) => {
          const isActiveProject = activeProject.id === project.id;

          return (
            <Grid
              key={project.id}
              style={{
                display: `flex`,
                alignItems: `center`,
                padding: `0 12px`,
                cursor: isActiveProject ? `default` : `pointer`,
                borderBottom: `1px solid ${RoutalPalette.neutral10}`,
                width: `100%`,
                height: `64px`,
                opacity: isActiveProject ? 0.5 : 1,
                pointerEvents: isActiveProject ? `none` : `auto`,
                justifyContent: `space-between`,
              }}
              onClick={async () => {
                if (isActiveProject) return;
                window.open(`${Routing.PLANNER.ROOT.replace(`:projectId`, project.id)}`, `_self`);
              }}
            >
              <Grid style={{ display: `flex` }}>
                <RoutalAvatar
                  url={project.avatar_url}
                  getUploadAsBase64={getUploadAsBase64}
                  label={project.label}
                  labelStyles={{ fontSize: `14px`, lineHeight: 2 }}
                />
                <HSpacer small />
                <Grid style={{ display: `flex`, flexDirection: `column` }}>
                  <Text
                    style={{
                      fontSize: `14px`,
                      color: RoutalPalette.primary80,
                      cursor: isActiveProject ? `default` : `pointer`,
                    }}
                  >
                    {project?.label}
                  </Text>

                  <VSpacer small />

                  <Grid style={{ display: `flex`, alignItems: `center` }}>
                    <ProjectUserIcon width="16px" height="16px" style={{ marginRight: `4px` }} />
                    <Text
                      variant={`label`}
                      style={{
                        fontSize: `12px`,
                        color: RoutalPalette.secondary20,
                        cursor: isActiveProject ? `default` : `pointer`,
                      }}
                      intlId="projects.users"
                      intlValues={{ num: project.users ?? 0 }}
                    />

                    <HSpacer medium />

                    <ProjectVehicleIcon width="16px" height="16px" style={{ marginRight: `4px` }} />
                    <Text
                      variant={`label`}
                      style={{
                        fontSize: `12px`,
                        color: RoutalPalette.secondary20,
                        cursor: isActiveProject ? `default` : `pointer`,
                      }}
                      intlId="projects.vehicles"
                      intlValues={{
                        num: project ? organizationResources?.getProjectResources(project.id)?.vehicles ?? 0 : 0,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              {!isActiveProject && (
                <ArrowForwardRoundedIcon style={{ color: RoutalPalette.primary40, width: `28px`, height: `28px` }} />
              )}
            </Grid>
          );
        })}
      </Grid>
    </RoutalDialog>
  );
};
